import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { FormsModule } from '@angular/forms';
import { ProfitLoss1Component } from "../profit-loss1/profit-loss1.component";
import { ProfitLoss2Component } from "../profit-loss2/profit-loss2.component";
import { FirmService } from '../../../../Services/firm.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-profit-loss',
  imports: [CommonModule, FormsModule, ProfitLoss1Component, ProfitLoss2Component],
  templateUrl: './profit-loss.component.html',
  styleUrl: './profit-loss.component.css'
})
export class ProfitLossComponent implements OnInit, OnDestroy {

  // for firm
  firms: any[] = [];

  filters: any = {
    firm_id: '',
    from_date: '',
    to_date: ''
  };

  // Default view
  selectedView: 'summary' | 'detailed' = 'summary';

  private firmSubscription: Subscription | null = null;

  @ViewChild('detailedView') detailedView!: ProfitLoss1Component;
  @ViewChild('summaryView') summaryView!: ProfitLoss2Component;

  constructor(
    private firmService: FirmService,
    private firmSelectionService: FirmSelectionService
  ) { }

  ngOnInit(): void {
    this.loadFirms();

    // Subscribe to global firm selection for default and restriction
    this.firmSubscription = this.firmSelectionService.selectedFirmName$.subscribe(firm => {
      if (firm) {
        this.filters.firm_id = firm.id;
        // Re-filter firms just in case
        this.loadFirms();
        this.onFilterChange();
      }
    });

    // Initial check from service
    const currentFirm = this.firmSelectionService.getselectedFirmName();
    if (currentFirm) {
      this.filters.firm_id = currentFirm.id;
      this.onFilterChange();
    }
  }

  ngOnDestroy(): void {
    if (this.firmSubscription) {
      this.firmSubscription.unsubscribe();
    }
  }

  loadFirms() {
    this.firmService.getFirms().subscribe({
      next: (res: any) => {
        const allFirms = res.data || [];
        const currentFirm = this.firmSelectionService.getselectedFirmName();

        if (currentFirm) {
          // Filter to show only the logged-in/selected firm
          this.firms = allFirms.filter((f: any) => f.id == currentFirm.id);
          if (this.firms.length > 0) {
            this.filters.firm_id = this.firms[0].id;
          }
        } else {
          this.firms = allFirms;
        }

        if (this.filters.firm_id) {
          this.onFilterChange();
        }
      },
      error: (err) => console.error('Error loading firms', err),
    });
  }

  onFilterChange() {
    this.filters = { ...this.filters };
  }

  get selectedFirmName(): string {
    const firm = this.firms.find(f => f.id == this.filters.firm_id);
    return firm ? firm.name : 'All Firms';
  }

  printProfitLoss() {
    const activeComponent = this.selectedView === 'detailed' ? this.detailedView : this.summaryView;
    if (!activeComponent || !activeComponent.data) {
      alert('No data available to print');
      return;
    }

    const data = activeComponent.data;
    const firmName = this.selectedFirmName;
    const fromDate = this.filters.from_date ? new Date(this.filters.from_date).toLocaleDateString('en-GB') : 'Start';
    const toDate = this.filters.to_date ? new Date(this.filters.to_date).toLocaleDateString('en-GB') : 'End';
    const generatedOn = new Date().toLocaleString('en-GB');

    const formatNum = (num: number) => {
      return (num || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    let printHtml = `
      <html>
      <head>
        <title>Profit & Loss - ${firmName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 13px; color: #333; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #0D5BAB; padding-bottom: 10px; }
          .header h2 { margin: 0; color: #0D5BAB; text-transform: uppercase; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
          .info-item { font-weight: bold; }
          .info-item span { font-weight: normal; }
          
          .section-title { background: #f4f4f4; padding: 5px 10px; font-weight: bold; border: 1px solid #ddd; border-bottom: 0; text-align: center; font-size: 15px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; table-layout: fixed; }
          th, td { border: 1px solid #ddd; padding: 6px 10px; text-align: left; }
          th { background-color: #f9f9f9; color: #0D5BAB; font-weight: bold; }
          .text-right { text-align: right; }
          .total-row { background-color: #FED40240; font-weight: bold; border-top: 2px solid #977F06; }
          .profit { color: #0bb60b; font-weight: bold; }
          .loss { color: #bb0000; font-weight: bold; }
          @media print { @page { margin: 10mm; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>Profit & Loss </h2>
          <p>${firmName}</p>
        </div>
        <div class="info-grid">
          <div class="info-item">Period: <span>${fromDate} to ${toDate}</span></div>
          <div class="info-item">Generated On: <span>${generatedOn}</span></div>
        </div>
    `;

    const renderTable = (title: string, accountData: any) => {
      let html = `<div class="section-title">${title}</div>`;
      html += `<table>
        <thead>
          <tr>
            <th>Expenditure <span style="float:right">Amount</span></th>
            <th>Revenue <span style="float:right">Amount</span></th>
          </tr>
        </thead>
        <tbody>`;

      const rows = activeComponent.getRows(accountData.expenditure, accountData.revenue);
      rows.forEach(row => {
        html += `<tr>
          <td>${row.exp ? `<div style="display:flex; justify-content:space-between;"><span>${row.exp.name}</span><span>${formatNum(row.exp.amount)}</span></div>` : ''}</td>
          <td>${row.rev ? `<div style="display:flex; justify-content:space-between;"><span>${row.rev.name}</span><span>${formatNum(row.rev.amount)}</span></div>` : ''}</td>
        </tr>`;
      });

      // Special handling for Gross/Net Profit/Loss
      if (accountData.gross_profit !== undefined) {
        if (accountData.gross_profit > 0 || accountData.gross_loss > 0) {
          html += `<tr>
              <td>${accountData.gross_profit > 0 ? `<div style="display:flex; justify-content:space-between;" class="profit"><span>Gross Profit</span><span>${formatNum(accountData.gross_profit)}</span></div>` : ''}</td>
              <td>${accountData.gross_loss > 0 ? `<div style="display:flex; justify-content:space-between;" class="loss"><span>Gross Loss</span><span>${formatNum(accountData.gross_loss)}</span></div>` : ''}</td>
            </tr>`;
        }
      } else if (accountData.net_profit !== undefined) {
        if (accountData.net_profit > 0 || accountData.net_loss > 0) {
          html += `<tr>
              <td>${accountData.net_profit > 0 ? `<div style="display:flex; justify-content:space-between;" class="profit"><span>Net Profit</span><span>${formatNum(accountData.net_profit)}</span></div>` : ''}</td>
              <td>${accountData.net_loss > 0 ? `<div style="display:flex; justify-content:space-between;" class="loss"><span>Net Loss</span><span>${formatNum(accountData.net_loss)}</span></div>` : ''}</td>
            </tr>`;
        }
      }

      html += `</tbody>
        <tfoot>
          <tr class="total-row">
            <td><div style="display:flex; justify-content:space-between;"><span>Total</span><span>₹ ${formatNum(accountData.total_expenditure)}</span></div></td>
            <td><div style="display:flex; justify-content:space-between;"><span>Total</span><span>₹ ${formatNum(accountData.total_revenue)}</span></div></td>
          </tr>
        </tfoot>
      </table>`;
      return html;
    };

    if (this.selectedView === 'detailed') {
      printHtml += renderTable('Trading Account', data.trading_account);
      printHtml += renderTable('Profit & Loss Account', data.profit_loss_account);
      // Capital Account removed per client requirement
    } else {
      // Summary View
      const summaryData = {
        expenditure: data.profit_loss_account.expenditure,
        revenue: data.profit_loss_account.revenue,
        net_profit: data.profit_loss_account.net_profit,
        net_loss: data.profit_loss_account.net_loss,
        total_expenditure: data.profit_loss_account.total_expenditure,
        total_revenue: data.profit_loss_account.total_revenue
      };

      printHtml += renderTable('Profit & Loss', summaryData);
      // Capital Account removed per client requirement
    }

    printHtml += `
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          }
        </script>
      </body>
      </html>
    `;

    const printWin = window.open('', '_blank');
    if (printWin) {
      printWin.document.open();
      printWin.document.write(printHtml);
      printWin.document.close();
    }
  }

  exportToExcel() {
    const activeComponent = this.selectedView === 'detailed' ? this.detailedView : this.summaryView;
    if (!activeComponent || !activeComponent.data) {
      alert('No data available to export');
      return;
    }

    const data = activeComponent.data;
    const firmName = this.selectedFirmName;
    const fromDate = this.filters.from_date || 'Start';
    const toDate = this.filters.to_date || 'End';
    const reportPeriod = `${fromDate} to ${toDate}`;

    const exports: any[] = [];

    const addSectionToExport = (title: string, accountData: any) => {
      // Header for section
      exports.push({ 'Expenditure': title, 'Amount (Exp)': '', 'Revenue': '', 'Amount (Rev)': '' });

      const rows = activeComponent.getRows(accountData.expenditure, accountData.revenue);
      rows.forEach(row => {
        exports.push({
          'Expenditure': row.exp ? row.exp.name : '',
          'Amount (Exp)': row.exp ? parseFloat(row.exp.amount.toString()) : 0,
          'Revenue': row.rev ? row.rev.name : '',
          'Amount (Rev)': row.rev ? parseFloat(row.rev.amount.toString()) : 0
        });
      });

      // Profit/Loss row
      if (accountData.gross_profit !== undefined) {
        if (accountData.gross_profit > 0 || accountData.gross_loss > 0) {
          exports.push({
            'Expenditure': accountData.gross_profit > 0 ? 'Gross Profit' : '',
            'Amount (Exp)': accountData.gross_profit > 0 ? parseFloat(accountData.gross_profit.toString()) : 0,
            'Revenue': accountData.gross_loss > 0 ? 'Gross Loss' : '',
            'Amount (Rev)': accountData.gross_loss > 0 ? parseFloat(accountData.gross_loss.toString()) : 0
          });
        }
      } else if (accountData.net_profit !== undefined) {
        if (accountData.net_profit > 0 || accountData.net_loss > 0) {
          exports.push({
            'Expenditure': accountData.net_profit > 0 ? 'Net Profit' : '',
            'Amount (Exp)': accountData.net_profit > 0 ? parseFloat(accountData.net_profit.toString()) : 0,
            'Revenue': accountData.net_loss > 0 ? 'Net Loss' : '',
            'Amount (Rev)': accountData.net_loss > 0 ? parseFloat(accountData.net_loss.toString()) : 0
          });
        }
      }

      // Total row
      exports.push({
        'Expenditure': 'Total',
        'Amount (Exp)': parseFloat(accountData.total_expenditure.toString()),
        'Revenue': 'Total',
        'Amount (Rev)': parseFloat(accountData.total_revenue.toString())
      });

      exports.push({}); // Space between sections
    };

    if (this.selectedView === 'detailed') {
      addSectionToExport('Trading Account', data.trading_account);
      addSectionToExport('Profit & Loss Account', data.profit_loss_account);
      // Capital Account removed per client requirement
    } else {
      // Summary View
      const summaryData = {
        expenditure: data.profit_loss_account.expenditure,
        revenue: data.profit_loss_account.revenue,
        net_profit: data.profit_loss_account.net_profit,
        net_loss: data.profit_loss_account.net_loss,
        total_expenditure: data.profit_loss_account.total_expenditure,
        total_revenue: data.profit_loss_account.total_revenue
      };

      addSectionToExport('Profit & Loss', summaryData);
      // Capital Account removed per client requirement
    }

    // Initialize Worksheet
    const worksheet = XLSX.utils.json_to_sheet([]);

    // Add Professional Header Rows (Rows 1-5)
    XLSX.utils.sheet_add_aoa(worksheet, [
      [`${firmName.toUpperCase()} - PROFIT & LOSS`],
      [`PERIOD: ${reportPeriod}`],
      [`GENERATED ON: ${new Date().toLocaleString('en-GB')}`],
      [] // Spacer
    ], { origin: "A1" });

    // Add Table Data
    XLSX.utils.sheet_add_json(worksheet, exports, { origin: "A5", skipHeader: false });

    // Set column widths
    worksheet['!cols'] = [
      { wch: 35 }, // Expenditure Label
      { wch: 20 }, // Amount Exp
      { wch: 35 }, // Revenue Label
      { wch: 20 }, // Amount Rev
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Profit & Loss');

    const fileName = `Profit_Loss_${firmName.replace(/\s+/g, '_')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }
}
